import React, { useState, useEffect } from 'react';
import { formatCurrency } from '../utils/currency';

function InvoiceEditor({
  mode = 'create',
  initialInvoice = null,
  clients = [],
  cases = [],
  filteredCases = [],
  setFilteredCases = () => {},
  serviceProducts = [],
  invoiceStatusOptions = [],
  isSaving = false,
  onCancel = () => {},
  onSubmit = async () => {},
  title
}) {
  const blank = { client: '', case: '', issue_date: '', due_date: '', status: 'Draft', invoice_items: [] };
  const [invoice, setInvoice] = useState(initialInvoice ? { ...initialInvoice } : { ...blank });
  const [newItem, setNewItem] = useState({ service_product: '', quantity: 1, unit_price: '', description: '', tax_type: 'STANDARD', discount_percent: 0 });

  // Once an invoice is fully Paid its line items are locked server-side
  // (InvoiceSerializer.update() rejects any `items` payload for a Paid
  // invoice) — mirror that here so the UI doesn't invite an edit that will
  // just 400, and so Save can omit `items` entirely, letting notes/due-date
  // edits still go through.
  const itemsLocked = mode === 'edit' && invoice.status === 'Paid';

  useEffect(() => {
    setInvoice(initialInvoice ? { ...initialInvoice } : { ...blank });
  }, [initialInvoice]);

  const handleInvoiceInputChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...invoice, [name]: value };
    if (name === 'client' && value) {
      setFilteredCases(cases.filter(c => c.client === parseInt(value)));
      updated.case = '';
    }
    setInvoice(updated);
  };

  const handleNewItemChange = (e) => {
    const { name, value } = e.target;
    let updated = { ...newItem, [name]: value };
    if (name === 'service_product') {
      const selected = serviceProducts.find(sp => sp.id === parseInt(value));
      if (selected) {
        updated.description = selected.name;
        updated.unit_price = parseFloat(selected.price);
      } else {
        updated.description = '';
        updated.unit_price = '';
      }
    }
    setNewItem(updated);
  };

  const addInvoiceItem = () => {
    if (newItem.service_product || newItem.description) {
      const unitPrice = parseFloat(newItem.unit_price);
      if (isNaN(unitPrice) || unitPrice <= 0) return;
      const itemToAdd = {
        service_product: newItem.service_product ? parseInt(newItem.service_product) : null,
        description: newItem.description || '',
        quantity: parseFloat(newItem.quantity),
        unit_price: unitPrice,
        discount_percent: parseFloat(newItem.discount_percent) || 0,
        tax_type: newItem.tax_type || 'STANDARD',
      };
      setInvoice(prev => ({ ...prev, invoice_items: [...(prev.invoice_items || []), itemToAdd] }));
      setNewItem({ service_product: '', quantity: 1, unit_price: '', description: '', tax_type: 'STANDARD', discount_percent: 0 });
    }
  };

  const removeInvoiceItem = (indexToRemove) => {
    setInvoice(prev => ({ ...prev, invoice_items: (prev.invoice_items || []).filter((_, i) => i !== indexToRemove) }));
  };

  const handleItemFieldChange = (index, e) => {
    const { name, value } = e.target;
    setInvoice(prev => {
      const items = [...(prev.invoice_items || [])];
      const current = { ...(items[index] || {}) };
      current[name] = value;
      if (name === 'service_product') {
        const selected = serviceProducts.find(sp => sp.id === parseInt(value));
        if (selected) {
          current.description = selected.name;
          current.unit_price = parseFloat(selected.price);
        } else {
          current.service_product = null;
        }
      }
      items[index] = current;
      return { ...prev, invoice_items: items };
    });
  };

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const payload = {
      ...invoice,
      client: parseInt(invoice.client) || null,
      case: invoice.case ? parseInt(invoice.case) : null,
    };
    if (itemsLocked) {
      // Omit `items` entirely rather than resubmitting the unchanged list —
      // the backend treats "items key present" as "please sync items" and
      // rejects that outright for a Paid invoice. Leaving it out lets
      // notes/due-date edits still save.
      delete payload.invoice_items;
    } else {
      payload.items = (invoice.invoice_items || []).map(item => ({
        ...item,
        service_product: item.service_product?.id || item.service_product || null,
        tax_type: item.tax_type || 'STANDARD',
      }));
    }
    await onSubmit(payload);
  };

  return (
    <div className="fixed inset-0 overflow-hidden z-50">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onCancel}></div>
        <section className="absolute inset-y-0 right-0 pl-10 max-w-full flex">
          <div className="w-screen max-w-2xl">
            <div className="h-full flex flex-col bg-white shadow-xl">
              <form onSubmit={submit} className="flex-1 flex flex-col min-h-0">
                <div className="px-4 py-6 sm:px-6 bg-gray-50 border-b flex-shrink-0">
                  <div className="flex items-start justify-between">
                    <h2 className="text-lg font-medium text-gray-900">{title || (mode === 'create' ? 'New Invoice' : `Edit Invoice ${invoice.invoice_number || ''}`)}</h2>
                    <div className="ml-3 h-7 flex items-center">
                      <button type="button" onClick={onCancel} className="bg-gray-50 rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <span className="sr-only">Close panel</span>
                        <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                    <div className="p-6 space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Client:</label>
                          <select name="client" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md" value={invoice.client || ''} onChange={handleInvoiceInputChange} required disabled={mode === 'edit'}>
                            <option value="">Select Client</option>
                            {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name || c.organization_name}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Case (Optional):</label>
                          <select name="case" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md" value={invoice.case || ''} onChange={handleInvoiceInputChange}>
                            <option value="">Select Case</option>
                            {filteredCases.map(ci => <option key={ci.id} value={ci.id}>{ci.title}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Issue Date:</label>
                          <input type="date" name="issue_date" className="mt-1 block w-full" value={invoice.issue_date || ''} onChange={handleInvoiceInputChange} required />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Due Date:</label>
                          <input type="date" name="due_date" className="mt-1 block w-full" value={invoice.due_date || ''} onChange={handleInvoiceInputChange} required />
                        </div>
                      </div>

                      <h4 className="mt-4 mb-3 text-lg font-semibold text-gray-700">Invoice Items</h4>
                      {itemsLocked && (
                        <p className="mb-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                          This invoice is fully paid — line items can no longer be edited. Other fields (notes, due date, etc.) can still be saved.
                        </p>
                      )}
                      <div className="overflow-x-auto mb-3">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tax Type</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Disc%</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {(invoice.invoice_items || []).length === 0 ? (
                              <tr><td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">No items added yet.</td></tr>
                            ) : (
                              (invoice.invoice_items || []).map((item, index) => (
                                <tr key={index}>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.description}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                    <select
                                      className="block w-full px-2 py-1 bg-white border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:text-gray-400"
                                      name="tax_type"
                                      value={item.tax_type || 'STANDARD'}
                                      onChange={(e) => handleItemFieldChange(index, e)}
                                      disabled={itemsLocked}
                                    >
                                      <option value="STANDARD">Standard (VAT)</option>
                                      <option value="DISBURSEMENT_K">Disbursement (Group K — VAT excluded)</option>
                                    </select>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    <input
                                      type="number"
                                      className="block w-20 px-2 py-1 bg-white border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:text-gray-400"
                                      name="quantity"
                                      value={item.quantity}
                                      onChange={(e) => handleItemFieldChange(index, e)}
                                      min="0.01"
                                      step="0.01"
                                      disabled={itemsLocked}
                                    />
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    <input
                                      type="number"
                                      className="block w-24 px-2 py-1 bg-white border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:text-gray-400"
                                      name="unit_price"
                                      value={item.unit_price}
                                      onChange={(e) => handleItemFieldChange(index, e)}
                                      min="0.01"
                                      step="0.01"
                                      disabled={itemsLocked}
                                    />
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    <input
                                      type="number"
                                      className="block w-16 px-2 py-1 bg-white border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:text-gray-400"
                                      name="discount_percent"
                                      value={item.discount_percent || 0}
                                      onChange={(e) => handleItemFieldChange(index, e)}
                                      min="0"
                                      max="100"
                                      step="0.5"
                                      disabled={itemsLocked}
                                    />
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatCurrency((item.quantity || 0) * (item.unit_price || 0) * (1 - (item.discount_percent || 0) / 100))}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    {!itemsLocked && (
                                      <button type="button" className="text-red-600" onClick={() => removeInvoiceItem(index)}>Remove</button>
                                    )}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>

                      {!itemsLocked && (
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end p-4 border rounded-md">
                          <div className="md:col-span-3">
                            <label className="block text-sm font-medium text-gray-700">Service/Product:</label>
                            <select name="service_product" className="mt-1 block w-full" value={newItem.service_product} onChange={handleNewItemChange}>
                              <option value="">Select...</option>
                              {serviceProducts.map(sp => (<option key={sp.id} value={sp.id}>{sp.name}</option>))}
                            </select>
                          </div>
                          <div className="md:col-span-3">
                            <label className="block text-sm font-medium text-gray-700">Custom Desc:</label>
                            <input type="text" name="description" className="mt-1 block w-full" value={newItem.description} onChange={handleNewItemChange} />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700">Tax Type:</label>
                            <select name="tax_type" className="mt-1 block w-full" value={newItem.tax_type} onChange={handleNewItemChange}>
                              <option value="STANDARD">Standard (VAT)</option>
                              <option value="DISBURSEMENT_K">Disbursement (Group K — VAT excluded)</option>
                            </select>
                          </div>
                          <div className="md:col-span-1">
                            <label className="block text-sm font-medium text-gray-700">Qty:</label>
                            <input type="number" name="quantity" className="mt-1 block w-full" value={newItem.quantity} onChange={handleNewItemChange} min="0.01" step="0.01" />
                          </div>
                          <div className="md:col-span-1">
                            <label className="block text-sm font-medium text-gray-700">Price:</label>
                            <input type="number" name="unit_price" className="mt-1 block w-full" value={newItem.unit_price} onChange={handleNewItemChange} min="0.01" step="0.01" />
                          </div>
                          <div className="md:col-span-1">
                            <label className="block text-sm font-medium text-gray-700">Disc%:</label>
                            <input type="number" name="discount_percent" className="mt-1 block w-full" value={newItem.discount_percent} onChange={handleNewItemChange} min="0" max="100" step="0.5" />
                          </div>
                          <div className="md:col-span-2">
                            <button type="button" className="w-full bg-blue-600 text-white px-4 py-2 rounded-md" onClick={addInvoiceItem}>Add Item</button>
                          </div>
                        </div>
                      )}

                    </div>
                </div>
                <div className="flex-shrink-0 px-4 py-4 flex justify-end space-x-4 border-t">
                  <button type="button" onClick={onCancel} className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md">Cancel</button>
                  <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded-md" disabled={isSaving}>{isSaving ? 'Saving...' : (mode === 'create' ? 'Create Invoice' : 'Save Changes')}</button>
                </div>
              </form>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default InvoiceEditor;
